// Twin of pricingSchemas.cjs — edit both; test enforces parity
// (backend/test/pricingSchemasParity.test.js).
//
// The real, enforcing copy of these schemas — backend/src/routes/
// pricingRoutes.js requires pricingSchemas.cjs (this file's CommonJS twin,
// same directory) instead of hand-duplicating them inline. Two files, not
// one, because Jest's module loader refuses to require() this ES module
// from a CJS test regardless of Node version (PROGRESS.md entry 138) even
// though plain Node handles it fine — see pricingSchemas.cjs's own header
// for the full reasoning and what would let the twin be deleted later.
//
// Used by admin/'s pricing workspace form (react-hook-form field-level
// `validate` callbacks) for the exact same client-side pre-check the server
// will apply, so a sales user sees a validation error before round-tripping
// to the API — never as a replacement for the server's own validation.

import { z } from 'zod';

// qty is DECIMAL(12,3).
export const qtySchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toString() : v.trim()))
  .refine((v) => /^\d{1,9}(\.\d{1,3})?$/.test(v), {
    message: 'Must be a positive number with at most 3 decimal places.',
  })
  .refine((v) => Number(v) > 0, { message: 'Must be greater than zero.' });

// unit_price is DECIMAL(12,2). Zero is legal (e.g. a promotional freebie
// line) — matches ck_qline_price_nonneg, not a stricter > 0 rule.
export const unitPriceSchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toString() : v.trim()))
  .refine((v) => /^\d{1,10}(\.\d{1,2})?$/.test(v), {
    message: 'Must be a non-negative number with at most 2 decimal places.',
  });

export const descriptionSchema = z.string().trim().min(1).max(500);
// max 30 mirrors request_lines.raw_unit VARCHAR(30) — the same DB column and
// client limit portal/'s requestLineSchema.js enforces (RAW_UNIT_MAX). This
// schema previously capped at 20, which was stricter than the constraint it
// is meant to mirror (audit finding, unit-dropdown task).
export const unitSchema = z.string().trim().min(1).max(30).optional();
export const discountReasonSchema = z.string().trim().min(1).max(500).optional();

export const lineSchema = z.object({
  requestLineId: z.coerce.number().int().positive().optional(),
  productId: z.coerce.number().int().positive().optional(),
  description: descriptionSchema,
  unit: unitSchema,
  qty: qtySchema,
  unitPrice: unitPriceSchema,
  discountReason: discountReasonSchema,
});

// Small, form-friendly wrappers: return { valid, message } instead of
// zod's own SafeParseReturnType shape, since react-hook-form's `validate`
// callback wants `true | string` (string = the error message to show), not
// a zod result object.
export function validateQty(value) {
  const result = qtySchema.safeParse(value);
  return result.success ? true : result.error.issues[0]?.message || 'Invalid quantity.';
}

export function validateUnitPrice(value) {
  const result = unitPriceSchema.safeParse(value);
  return result.success ? true : result.error.issues[0]?.message || 'Invalid unit price.';
}

// Blank is explicitly valid — unit is optional (unitSchema's own .optional()
// only covers `undefined`, not the empty string a cleared text input sends),
// same blank-is-ok shape PricingLineRow.jsx's own validateQtyOrBlank/
// validateUnitPriceOrBlank already use for the adjacent qty/unitPrice
// fields. Added because the unit input in PricingLineRow.jsx previously had
// no validation at all, unlike every other field in that row (audit
// finding, unit-dropdown task).
export function validateUnit(value) {
  if (value === undefined || value === null || value === '') return true;
  const result = unitSchema.safeParse(value);
  return result.success ? true : result.error.issues[0]?.message || 'Invalid unit.';
}
